import { Component, OnInit, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { NotificationService } from '../../../services/notification.service';
import { ToastService } from '../../../services/toast.service';
import { Notification, NotificationType } from '../../../interfaces/api-interfaces';
import { Subscription , firstValueFrom} from 'rxjs';

@Component({
  selector: 'app-notification-panel',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './notification-panel.component.html',
  styleUrl: './notification-panel.component.css'
})
export class NotificationPanelComponent implements OnInit {
  @Output() closePanel = new EventEmitter<void>();

  private readonly notificationService = inject(NotificationService);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);

  notifications: Notification[] = [];
  loading = false;

  private notificationsSubscription?: Subscription;
  private loadingSubscription?: Subscription;

  // NotificationType enum for template
  NotificationType = NotificationType;

  ngOnInit() {
    // Subscribe to notifications
    this.notificationsSubscription = this.notificationService.notifications$.subscribe(
      notifications => {
        this.notifications = notifications;
      }
    );

    // Subscribe to loading state
    this.loadingSubscription = this.notificationService.loading$.subscribe(
      loading => {
        this.loading = loading;
      }
    );
  }

  ngOnDestroy() {
    this.notificationsSubscription?.unsubscribe();
    this.loadingSubscription?.unsubscribe();
  }

  getNotificationIcon(type: NotificationType): string {
    switch (type) {
      case NotificationType.CHANNEL_MESSAGE:
        return 'fas fa-comments';
      case NotificationType.DIRECT_MESSAGE:
        return 'fas fa-envelope';
      case NotificationType.BUDDY_MESSAGE:
        return 'fas fa-user-friends';
      case NotificationType.ANNOUNCEMENT:
        return 'fas fa-bullhorn';
      case NotificationType.MENTION:
        return 'fas fa-at';
      case NotificationType.REACTION:
        return 'fas fa-heart';
      default:
        return 'fas fa-bell';
    }
  }

  getNotificationColor(type: NotificationType): string {
    switch (type) {
      case NotificationType.CHANNEL_MESSAGE:
        return 'text-primary';
      case NotificationType.DIRECT_MESSAGE:
        return 'text-secondary';
      case NotificationType.BUDDY_MESSAGE:
        return 'text-accent';
      case NotificationType.ANNOUNCEMENT:
        return 'text-warning';
      case NotificationType.MENTION:
        return 'text-info';
      case NotificationType.REACTION:
        return 'text-error';
      default:
        return 'text-base-content';
    }
  }

  getFormattedTime(notification: Notification): string {
    const date = new Date(notification.createdAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) {
      return 'Gerade eben';
    } else if (diffMins < 60) {
      return `vor ${diffMins} Min.`;
    } else if (diffHours < 24) {
      return `vor ${diffHours} Std.`;
    } else if (diffDays < 7) {
      return `vor ${diffDays}T`;
    } else {
      return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: 'short'
      });
    }
  }

  async handleNotificationClick(notification: Notification) {
    if (!notification.read) {
      try {
        await firstValueFrom(this.notificationService.markAsRead(notification.notificationId));
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }

    const queryParams = this.buildCommunityQueryParams(notification);
    if (queryParams) {
      this.router.navigate(['/community'], { queryParams: { ...queryParams, _ts: Date.now() } });
      this.closePanel.emit();
      return;
    }

    if (notification.actionUrl) {
      this.router.navigateByUrl(notification.actionUrl);
      this.closePanel.emit();
    }
  }

  openSettings() {
    this.router.navigate(['/settings/notifications']);
    this.closePanel.emit();
  }

  private buildCommunityQueryParams(notification: Notification): Record<string, string> | null {
    const url = notification.actionUrl;
    if (!url) return null;

    // /community/{communityId}/channel/{channelId}
    const channelMatch = url.match(/^\/community\/([^/]+)\/channel\/([^/]+)/);
    if (channelMatch) {
      const params: Record<string, string> = { communityId: channelMatch[1], channelId: channelMatch[2] };
      if (notification.referenceId) params['messageId'] = notification.referenceId;
      return params;
    }

    // /buddy-groups/{buddyGroupId}
    const buddyMatch = url.match(/^\/buddy-groups\/([^/]+)/);
    if (buddyMatch) {
      const params: Record<string, string> = { buddyGroupId: buddyMatch[1] };
      if (notification.referenceId) params['messageId'] = notification.referenceId;
      return params;
    }

    // /messages/{conversationId}
    const dmMatch = url.match(/^\/messages\/([^/]+)/);
    if (dmMatch) {
      return { conversationId: dmMatch[1] };
    }

    return null;
  }

  async markAllAsRead() {
    try {
      await firstValueFrom(this.notificationService.markAllAsRead());
      this.toastService.success('Alle gelesen');
    } catch (error) {
      console.error('Error marking all as read:', error);
      this.toastService.error('Fehler', 'Konnte Benachrichtigungen nicht markieren');
    }
  }

  async dismissNotification(event: Event, notification: Notification) {
    event.stopPropagation(); // Prevent navigation

    try {
      await firstValueFrom(this.notificationService.dismissNotification(notification.notificationId));
    } catch (error) {
      console.error('Error dismissing notification:', error);
      this.toastService.error('Fehler', 'Konnte Benachrichtigung nicht entfernen');
    }
  }

  get hasUnread(): boolean {
    return this.notifications.some(n => !n.read);
  }
}
